/**
 * The collection markup is only a stand-in while the Constructor.io
 * replacement is in flight. A/B mode keeps the complete Liquid grid in the
 * document; forced mode sends lightweight product and filter placeholders and
 * loads the real Shopify grid and facets only if Constructor falls back. The
 * product and filter placeholders carry `data-constructor-skeleton`, and the
 * rules in `src/tailwind/components/product-card.css` and
 * `form-filter-controls.css` grey out everything that does. The Shopify product
 * count remains visible and is replaced directly by the Constructor response.
 * Removing the attribute is the whole placeholder contract -- it reveals any
 * remaining Shopify stand-in; forced-mode fallback first replaces its
 * lightweight placeholders with complete Shopify markup.
 *
 * One attribute for every stand-in, deliberately: the shape of a placeholder
 * comes from the element it sits on (`.twc-product-card[data-constructor-skeleton]`,
 * `[data-filter-group][data-constructor-skeleton]`, and so on), so putting a
 * new element on a skeleton is a change in Liquid and CSS only. There is no
 * list here to keep in step with the templates, which is what a per-element
 * attribute would need -- and would eventually be forgotten.
 *
 * A response with results takes the product placeholders away by replacing the
 * markup that carries them, but nothing else does. An empty result set, a
 * failed browse request, a widget chunk that never loads, and the filter
 * groups of a template that has no Constructor facet outlet would all leave
 * the collection pulsing grey forever, so every one of those paths ends here.
 */
import {
  CONSTRUCTOR_COLLECTION_FALLBACK_ATTRIBUTE,
  CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR,
} from './mode';

export const COLLECTION_SKELETON_ATTRIBUTE = 'data-constructor-skeleton';

/**
 * Sorting is hidden while the widget may take the collection over, and unlike
 * the skeletons it is revealed by whichever of the two owners ends up with the
 * collection -- because the two do not offer the same options.
 *
 * The options the Liquid controls list are Shopify's. Constructor states its own
 * in every browse response, so `renderSort.js` rebuilds the list from the
 * response and reveals the control on a take-over, and
 * `revealShopifyCollection()` reveals the Liquid options untouched on a
 * hand-back. Both templates stamp the attribute on the desktop select and on
 * the mobile sort group, so neither set flashes in during the decision.
 */
export const COLLECTION_SORT_HIDDEN_ATTRIBUTE =
  'data-constructor-collection-sort-hidden';

/**
 * Shopify and Constructor can both understand curated `filter.p.m.*` filters,
 * so Liquid keeps Shopify's active-facet summary ready for a possible
 * hand-back but hides it while Constructor owns the collection.
 */
export const COLLECTION_SHOPIFY_ACTIVE_FACETS_HIDDEN_ATTRIBUTE =
  'data-constructor-shopify-active-facets-hidden';

/**
 * Beacon attributes the Liquid collection parks until the decision is made.
 *
 * The Constructor beacon must not be handed a result count while a request for
 * results is in progress, and the Shopify count and page describe a browse
 * event Constructor never answered -- there is no result id to go with them. So
 * `snippets/gld_collection-product-list.liquid` renders them under
 * `data-cnstrc-fallback-*` names, and only the hand-back promotes them, which
 * is the moment the Shopify collection really is what the shopper is browsing.
 * A finished Constructor render writes the real attributes itself, from the
 * response.
 */
export const COLLECTION_FALLBACK_TRACKING_ATTRIBUTES = {
  'data-cnstrc-fallback-num-results': 'data-cnstrc-num-results',
  'data-cnstrc-fallback-result-page': 'data-cnstrc-result-page',
};

const COLLECTION_SECTION_SELECTOR = '[data-collection-section]';

/**
 * Strips one attribute off a collection and reports how many elements carried
 * it, so a caller can tell an actual fallback from a no-op.
 *
 * The lookup widens to the enclosing collection section because the filters and
 * the header controls sit beside the widget root, not inside it, in the
 * templates that render the drawer in Liquid.
 */
const removeCollectionAttribute = (element, attribute) => {
  const root = element?.closest?.(COLLECTION_SECTION_SELECTOR) || element;

  if (!root?.querySelectorAll) {
    return 0;
  }

  const selector = `[${attribute}]`;
  const nodes = [
    ...(root.matches?.(selector) ? [root] : []),
    ...root.querySelectorAll(selector),
  ];

  nodes.forEach((node) => node.removeAttribute(attribute));

  return nodes.length;
};

/** Reveals every current Shopify stand-in of one collection. */
export const clearCollectionSkeletons = (element) =>
  removeCollectionAttribute(element, COLLECTION_SKELETON_ATTRIBUTE);

/**
 * Offers the sort control again -- the Liquid options on a hand-back, the ones
 * `renderSort.js` has just rendered on a take-over.
 */
export const revealCollectionSort = (element) =>
  removeCollectionAttribute(element, COLLECTION_SORT_HIDDEN_ATTRIBUTE);

/** Reveals Shopify's active-facet summary when the collection is handed back. */
export const revealShopifyActiveFacets = (element) =>
  removeCollectionAttribute(
    element,
    COLLECTION_SHOPIFY_ACTIVE_FACETS_HIDDEN_ATTRIBUTE
  );

/**
 * Promotes the parked beacon attributes to the names the beacon reads, so a
 * collection that fell back reports the browse event it is actually showing.
 */
export const promoteFallbackTrackingAttributes = (element) => {
  const root = element?.closest?.(COLLECTION_SECTION_SELECTOR) || element;

  if (!root?.querySelectorAll) {
    return 0;
  }

  let promoted = 0;

  Object.entries(COLLECTION_FALLBACK_TRACKING_ATTRIBUTES).forEach(
    ([parked, name]) => {
      const selector = `[${parked}]`;
      const nodes = [
        ...(root.matches?.(selector) ? [root] : []),
        ...root.querySelectorAll(selector),
      ];

      nodes.forEach((node) => {
        node.setAttribute(name, node.getAttribute(parked));
        node.removeAttribute(parked);
        promoted += 1;
      });
    }
  );

  return promoted;
};

const dropParkedTrackingAttributes = (element) =>
  Object.keys(COLLECTION_FALLBACK_TRACKING_ATTRIBUTES).reduce(
    (dropped, parked) => dropped + removeCollectionAttribute(element, parked),
    0
  );

/**
 * Records on the widget root which of the two outcomes the collection is in.
 * `mode.js` reads it to decide whether the widget or Shopify owns the next
 * interaction, and the collection page re-runs the hand-back on it after every
 * Shopify re-render.
 */
const setFallbackState = (element, reason) => {
  const root = element?.closest?.(COLLECTION_SECTION_SELECTOR) || element;
  const widgets = [
    ...(root?.matches?.(CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR) ? [root] : []),
    ...(root?.querySelectorAll?.(CONSTRUCTOR_COLLECTION_WIDGET_SELECTOR) || []),
  ];

  widgets.forEach((widget) => {
    if (reason) {
      widget.setAttribute(CONSTRUCTOR_COLLECTION_FALLBACK_ATTRIBUTE, reason);
    } else {
      widget.removeAttribute(CONSTRUCTOR_COLLECTION_FALLBACK_ATTRIBUTE);
    }
  });
};

/**
 * What a finished Constructor render does to the collection around it: reveal
 * the stand-in it just replaced and drop the parked beacon attributes, whose
 * real counterparts the render has already written from the response. Sorting
 * is not here: `renderSort.js` reveals it, and only once it has replaced the
 * Shopify options with the ones the response offers.
 */
export const takeOverCollection = (element) => {
  setFallbackState(element, null);

  return clearCollectionSkeletons(element) + dropParkedTrackingAttributes(element);
};

/**
 * The complete hand-back to Shopify: reveal the Liquid stand-in, offer the
 * sorting again and let the beacon describe the Shopify browse event.
 *
 * These two are the only entry points, one per outcome, and never the
 * individual steps -- the steps are not interchangeable, and every render path
 * has to do all of the ones its outcome calls for. Splitting them is exactly how
 * the sort select was once left hidden on a collection that had already been
 * handed back. `revealCollectionSort()` is the one exception, because a
 * take-over has to rebuild the options before it may show them.
 */
export const revealShopifyCollection = (element, reason = 'error') => {
  setFallbackState(element, reason);

  return (
    clearCollectionSkeletons(element) +
    revealCollectionSort(element) +
    revealShopifyActiveFacets(element) +
    promoteFallbackTrackingAttributes(element)
  );
};

export default clearCollectionSkeletons;
